/**
 * Frontend entry point.
 *
 * Import order matters and is not incidental: jQuery must exist as a global
 * before Semantic UI and jquery-validation evaluate, because both close over the
 * free variable `jQuery` rather than importing it. ES modules evaluate
 * depth-first in import order, which is what makes this work.
 */
import './boot/jqueryGlobal';

// Semantic UI 2.4, vendored as a pre-built 28-component bundle. Consuming it
// needs no npm package, no gulp and no LESS toolchain — only these two files.
import './vendor/semantic/dist/semantic.min.css';
import './vendor/semantic/dist/semantic.min.js';

import 'jquery-validation';
import 'jquery-validation-unobtrusive';

// Custom rules, registered after the adapters they extend exist.
import './boot/validation';

import './styles/main.scss';

import { createApp } from 'vue';
import { initBehaviour } from './boot/behaviour';
import { initMainHeader } from './boot/mainheader';
import { initPages } from './boot/pages';
import { initSmartLoad } from './boot/smartload';
import { initReveal } from './lib/reveal';
import DemoPanel from './components/DemoPanel.vue';

/**
 * Vue components mount only where their host element exists, so a page that does
 * not use one pays nothing. Replaces the 2.2 "VueCandidate" class, which did the
 * same thing with more ceremony.
 */
const MOUNTS: ReadonlyArray<{ id: string; component: Parameters<typeof createApp>[0] }> = [
  { id: 'demopanel', component: DemoPanel },
];

function mountComponents(): void {
  for (const { id, component } of MOUNTS) {
    const host = document.getElementById(id);

    if (host) {
      createApp(component).mount(host);
    }
  }
}

function start(): void {
  initSmartLoad();
  initMainHeader();
  initBehaviour();
  initPages();
  initReveal();
  mountComponents();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
